import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bot, Play, Square, CheckCircle2, AlertTriangle, Loader2, Sparkles, FileText, Image as ImageIcon, BookOpenCheck, RefreshCw, Download, Terminal, Database, Brain, Globe2, FileSearch, AlertOctagon, Zap, ArrowDown, History, User as UserIcon, XCircle, MinusCircle, Clock, RotateCw, X as XIcon, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import KineticHero from '@/components/KineticHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_META = [
  { n: 1, label: 'Generate all missing sections',  icon: Sparkles },
  { n: 2, label: 'Heal incomplete sections (x2)',  icon: RefreshCw },
  { n: 3, label: 'Generate all illustrations',     icon: ImageIcon },
  { n: 4, label: 'Heal missing illustrations',     icon: ImageIcon },
  { n: 5, label: 'Generate PESTLE infographic',    icon: Sparkles },
  { n: 6, label: 'Generate references',            icon: BookOpenCheck },
  { n: 7, label: 'PDF ready to download',          icon: FileText },
];

const TIER_COLORS = { scout: '#2563eb', strategist: '#059669', sovereign: '#7c3aed' };

// Status badge styling for the run-history panel.
const RUN_STATUS_META = {
  completed:       { color: '#059669', bg: 'rgba(5,150,105,0.10)',  border: 'rgba(5,150,105,0.35)',  Icon: CheckCircle2, label: 'completed'       },
  running:         { color: '#2563eb', bg: 'rgba(37,99,235,0.10)',  border: 'rgba(37,99,235,0.35)',  Icon: Loader2,      label: 'running',         spin: true },
  stopped:         { color: '#64748b', bg: 'rgba(100,116,139,0.12)',border: 'rgba(100,116,139,0.35)',Icon: MinusCircle,  label: 'stopped'         },
  aborted_budget:  { color: '#d97706', bg: 'rgba(217,119,6,0.10)',  border: 'rgba(217,119,6,0.35)',  Icon: AlertTriangle,label: 'budget hit'      },
  failed:          { color: '#dc2626', bg: 'rgba(220,38,38,0.10)',  border: 'rgba(220,38,38,0.35)',  Icon: XCircle,      label: 'failed'          },
};

function formatDuration(secs) {
  if (secs == null) return '—';
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

// Color + icon map for the live backend activity stream.
const ACTIVITY_META = {
  info:    { color: '#94a3b8', bg: 'rgba(148,163,184,0.10)', Icon: Terminal,   label: 'info'   },
  llm:     { color: '#c4b5fd', bg: 'rgba(196,181,253,0.12)', Icon: Brain,      label: 'llm'    },
  db:      { color: '#5eead4', bg: 'rgba(94,234,212,0.10)',  Icon: Database,   label: 'db'     },
  image:   { color: '#fda4af', bg: 'rgba(253,164,175,0.10)', Icon: ImageIcon,  label: 'image'  },
  source:  { color: '#fcd34d', bg: 'rgba(252,211,77,0.10)',  Icon: FileSearch, label: 'source' },
  net:     { color: '#67e8f9', bg: 'rgba(103,232,249,0.10)', Icon: Globe2,     label: 'net'    },
  success: { color: '#6ee7b7', bg: 'rgba(110,231,183,0.12)', Icon: CheckCircle2, label: 'ok'   },
  warn:    { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  Icon: AlertTriangle, label: 'warn'},
  error:   { color: '#fca5a5', bg: 'rgba(252,165,165,0.12)', Icon: AlertOctagon, label: 'err' },
};

export default function AutomationPage() {
  const [countries, setCountries] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [status, setStatus] = useState({ running: false });
  const [starting, setStarting] = useState(false);

  // Live activity scroller state
  const [activity, setActivity] = useState([]);
  const lastSeqRef = useRef(0);
  const logEndRef = useRef(null);
  const logContainerRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Run history
  const [runs, setRuns] = useState([]);
  const [countryFilter, setCountryFilter] = useState(null);  // null | country name string
  const [drawerRun, setDrawerRun] = useState(null);          // full run detail (from /history/{id})
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Illustration provider — display-only here; the actual toggle lives on the User Management page.
  const [illustProvider, setIllustProvider] = useState('gemini');

  useEffect(() => {
    axios.get(`${API}/admin/config/illustration-provider`)
      .then(r => { setIllustProvider(r.data?.value || 'gemini'); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/countries`).then(r => setCountries(r.data || []));
  }, []);

  // Poll status every 3s
  useEffect(() => {
    const poll = async () => {
      try { const r = await axios.get(`${API}/admin/automation/status`); setStatus(r.data || {}); } catch {}
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, []);

  // Poll recent run history every 12s (refreshes faster while a run is live so users see it move → completed in near real-time)
  useEffect(() => {
    const fetchRuns = async () => {
      try {
        const r = await axios.get(`${API}/admin/automation/history`, { params: { limit: 15 } });
        setRuns(r.data?.runs || []);
      } catch {}
    };
    fetchRuns();
    const interval = status.running ? 4000 : 12000;
    const id = setInterval(fetchRuns, interval);
    return () => clearInterval(id);
  }, [status.running]);

  // Poll activity tail every 1.5s (lightweight: only sends entries newer than lastSeq)
  useEffect(() => {
    const tail = async () => {
      try {
        const r = await axios.get(`${API}/admin/automation/activity`, {
          params: { since_seq: lastSeqRef.current },
        });
        const entries = r.data?.entries || [];
        if (entries.length) {
          setActivity(prev => {
            const merged = [...prev, ...entries];
            // Cap UI buffer to last 300 for perf
            return merged.length > 300 ? merged.slice(merged.length - 300) : merged;
          });
          lastSeqRef.current = r.data.last_seq || lastSeqRef.current;
        } else if (r.data?.last_seq !== undefined && r.data.last_seq < lastSeqRef.current) {
          // Buffer was reset on backend (new run) — clear local too
          lastSeqRef.current = 0;
          setActivity([]);
        }
      } catch {}
    };
    tail();
    const id = setInterval(tail, 1500);
    return () => clearInterval(id);
  }, []);

  // Auto-scroll on new entries unless user scrolled up
  useEffect(() => {
    if (autoScroll && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [activity, autoScroll]);

  // Detect user scrolling up → disable auto-scroll; scrolling to bottom → re-enable
  const handleLogScroll = (e) => {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  };

  const start = async () => {
    if (!selectedId) return toast.error('Select a country first');
    setStarting(true);
    try {
      // Reset local tail state for the new run
      lastSeqRef.current = 0;
      setActivity([]);
      setAutoScroll(true);
      const r = await axios.post(`${API}/admin/automation/start`, { country_id: selectedId });
      toast.success(`Automation started for ${r.data.country_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start');
    } finally { setStarting(false); }
  };

  const stop = async () => {
    try { await axios.post(`${API}/admin/automation/stop`); toast.success('Stop requested'); } catch {}
  };

  const retryRun = async (run) => {
    if (status.running) {
      toast.error(`Stop the current run (${status.country_name}) before retrying`);
      return;
    }
    if (!run?.country_id) return;
    // Reset local scroller state
    lastSeqRef.current = 0;
    setActivity([]);
    setAutoScroll(true);
    setSelectedId(run.country_id);
    try {
      const r = await axios.post(`${API}/admin/automation/start`, { country_id: run.country_id });
      toast.success(`Retrying automation for ${r.data.country_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to retry');
    }
  };

  const openRunDetail = async (run_id) => {
    setDrawerLoading(true);
    setDrawerRun({ id: run_id });  // show skeleton immediately
    try {
      const r = await axios.get(`${API}/admin/automation/history/${run_id}`);
      setDrawerRun(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load run');
      setDrawerRun(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => { setDrawerRun(null); setDrawerLoading(false); };

  // Derived: runs filtered by country chip (if set)
  const visibleRuns = countryFilter
    ? runs.filter(r => r.country_name === countryFilter)
    : runs;

  const running = status.running;
  const pct = status.progress_pct || 0;
  const currentStep = status.current_step || 0;
  const currentCountry = status.country_name || countries.find(c => c.id === status.country_id)?.name || '—';
  const sectionsDone = status.sections_done || [];
  const errors = status.errors || [];

  const eta = useMemo(() => {
    if (!running || !status.started_at || pct <= 0) return null;
    const elapsedMs = Date.now() - new Date(status.started_at).getTime();
    const totalMs = elapsedMs / (pct / 100);
    const remainingMin = Math.max(0, Math.round((totalMs - elapsedMs) / 60000));
    return remainingMin;
  }, [running, pct, status.started_at]);

  return (
    <div className="min-h-screen bg-[#f8f9ff]" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="automation-page">
      <KineticHero
        uid="autom"
        palette="violet"
        eyebrow="Admin · one-click blueprint pipeline"
        title="Blueprint"
        accent="Automation"
        subtitle="Pick a country and run the full 7-step pipeline end-to-end — sections, healing, illustrations, references, PDF — in a single supervised pass."
      />

      <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
        {/* Control bar */}
        <div className="bg-white rounded-xl shadow-sm border border-[#c3c6d7]/30 p-4 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-[#7c3aed]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#434655]">Target country</span>
            </div>
            <Select value={selectedId} onValueChange={setSelectedId} disabled={running}>
              <SelectTrigger data-testid="automation-country-select" className="w-72 h-9 text-xs bg-[#f8f9ff]">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {countries.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name} — {c.sector || 'General'}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!running ? (
              <Button onClick={start} disabled={!selectedId || starting}
                data-testid="automation-start-btn"
                className="gap-2 bg-gradient-to-r from-[#004ac6] to-[#7c3aed] hover:brightness-110 text-white font-bold">
                <Play className="w-4 h-4" /> {starting ? 'Starting…' : 'Start automation'}
              </Button>
            ) : (
              <Button onClick={stop} variant="outline"
                data-testid="automation-stop-btn"
                className="gap-2 border-red-300 text-red-600 hover:bg-red-50">
                <Square className="w-4 h-4" /> Stop
              </Button>
            )}

            <div className="ml-auto flex items-center gap-3 text-[11px] text-[#434655]">
              {running && <><Loader2 className="w-3.5 h-3.5 animate-spin text-[#7c3aed]" /> <span className="font-semibold">Running on {currentCountry}</span></>}
              {!running && status.finished_at && <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> <span>Last run: {new Date(status.finished_at).toLocaleTimeString()}</span></>}
              {!running && !status.finished_at && <span>Idle</span>}
            </div>
          </div>

          {/* Illustration-engine hint — actual toggle lives on the User Management page to guarantee it's a single, global, app-wide setting. */}
          <div className="mt-3 pt-3 border-t border-dashed border-[#c3c6d7]/40 flex items-center gap-2 text-[10.5px] text-[#737686]" data-testid="automation-illust-engine-hint">
            <ImageIcon className="w-3.5 h-3.5 text-[#059669]" />
            <span className="font-bold uppercase tracking-widest text-[#434655]">Illustration engine</span>
            <span className="font-mono px-2 py-0.5 rounded-full border"
                  style={illustProvider === 'claude_html'
                    ? { color: '#7c3aed', borderColor: '#7c3aed55', background: '#7c3aed10' }
                    : { color: '#ea580c', borderColor: '#ea580c55', background: '#ea580c10' }}>
              {illustProvider === 'claude_html' ? 'Claude Sonnet 4.6 · HTML' : 'Gemini Nano Banana'}
            </span>
            <span className="text-[10px] italic">Change it on the User Management page · System Settings</span>
          </div>
        </div>

        {/* LIVE PROGRESS PANEL — dark aesthetic similar to the attachment */}
        <div className="rounded-xl overflow-hidden shadow-lg border border-[#1a2d4a]/20 mb-6" data-testid="automation-live-panel">
          <div className="bg-gradient-to-br from-[#0b1c30] via-[#12263f] to-[#1a2d4a] text-white p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
                 style={{ background: 'radial-gradient(circle, #7c3aed, transparent 70%)' }} />

            {/* Header row */}
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${running ? 'bg-emerald-400 animate-pulse' : 'bg-white/30'}`} />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Live Pipeline</span>
                <span className="text-lg font-extrabold tracking-tight" data-testid="automation-country-label">{currentCountry}</span>
              </div>
              <div className="flex items-center gap-6 text-[11px]">
                <div className="text-right">
                  <div className="text-[9px] text-white/40 uppercase tracking-widest">Progress</div>
                  <div className="text-2xl font-black font-mono" data-testid="automation-pct">{pct}%</div>
                </div>
                {eta != null && (
                  <div className="text-right">
                    <div className="text-[9px] text-white/40 uppercase tracking-widest">ETA</div>
                    <div className="text-sm font-bold">~{eta} min</div>
                  </div>
                )}
              </div>
            </div>

            {/* Main progress bar */}
            <div className="relative h-2.5 rounded-full bg-white/10 overflow-hidden mb-6" data-testid="automation-progress-bar">
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out"
                style={{
                  width: `${pct}%`,
                  background: 'linear-gradient(90deg, #38bdf8 0%, #7c3aed 50%, #ec4899 100%)',
                  boxShadow: '0 0 18px rgba(124,58,237,0.45)',
                }}
              />
              {running && (
                <div className="absolute inset-0 opacity-40 pointer-events-none"
                     style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', backgroundSize: '200% 100%', animation: 'automShimmer 1.8s linear infinite' }} />
              )}
            </div>
            <style>{`@keyframes automShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>

            {/* Step ladder */}
            <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
              {STEP_META.map(s => {
                const Icon = s.icon;
                const active = currentStep === s.n;
                const complete = currentStep > s.n || (!running && status.finished_at && currentStep === 7);
                return (
                  <div key={s.n} className={`relative rounded-lg p-2.5 border transition-all ${active ? 'bg-white/15 border-white/40 shadow-lg' : complete ? 'bg-emerald-500/15 border-emerald-400/40' : 'bg-white/5 border-white/10'}`} data-testid={`automation-step-${s.n}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      {complete ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> : active ? <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-200" /> : <Icon className="w-3.5 h-3.5 text-white/40" />}
                      <span className={`text-[10px] font-mono font-bold ${active ? 'text-white' : complete ? 'text-emerald-200' : 'text-white/40'}`}>STEP {s.n}</span>
                    </div>
                    <div className={`text-[10px] leading-tight ${active ? 'text-white' : complete ? 'text-emerald-100' : 'text-white/50'}`}>
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Current focus strip */}
            {running && (
              <div className="mt-5 flex items-center gap-3 bg-white/5 rounded-lg px-4 py-2.5 border border-white/10">
                <Loader2 className="w-4 h-4 animate-spin text-violet-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] uppercase tracking-widest text-white/40 mb-0.5">Step {currentStep} · {status.current_label || '—'}</div>
                  <div className="text-sm font-bold truncate" data-testid="automation-current-focus">
                    {status.current_section || 'Initialising…'}
                  </div>
                </div>
                {status.step_progress?.total > 0 && (
                  <div className="text-right text-xs font-mono text-white/80 whitespace-nowrap">
                    {status.step_progress.current} / {status.step_progress.total}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer: download PDF when done */}
          {!running && status.finished_at && status.country_id && (
            <div className="bg-white p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-xs text-[#434655]">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="font-semibold">Pipeline complete</span>
                <span>— {sectionsDone.length} section{sectionsDone.length !== 1 ? 's' : ''} generated · {errors.length} error{errors.length !== 1 ? 's' : ''}</span>
              </div>
              <button
                onClick={async () => {
                  try {
                    toast.loading('Generating PDF report — this may take up to 90 seconds…', { id: 'auto-pdf' });
                    const r = await axios.get(
                      `${API}/countries/${status.country_id}/blueprint/pdf?language=en`,
                      { responseType: 'blob', timeout: 300000 }
                    );
                    const url = window.URL.createObjectURL(new Blob([r.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `SA_${status.country_name || 'blueprint'}_${new Date().toISOString().split('T')[0]}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.dismiss('auto-pdf');
                    toast.success('PDF downloaded');
                  } catch (e) {
                    toast.dismiss('auto-pdf');
                    const st = e.response?.status;
                    if (st === 401 || st === 403) toast.error('Session expired — please log in again');
                    else if (e.code === 'ECONNABORTED') toast.error('PDF generation timed out — please retry');
                    else if (e.code === 'ERR_NETWORK' || /network error/i.test(e.message || '')) toast.error('PDF stream interrupted by the proxy — retry in a moment');
                    else toast.error(e.response?.data?.detail || `PDF error${st ? ` (HTTP ${st})` : ''}`);
                  }
                }}
                className="inline-flex items-center gap-2 bg-[#0b1c30] hover:bg-[#1a2d4a] text-white text-xs font-bold rounded-lg px-4 py-2 shadow-sm"
                data-testid="automation-download-pdf"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </button>
            </div>
          )}
        </div>

        {/* Errors panel */}
        {errors.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="automation-errors">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <span className="text-xs font-bold text-amber-800">Non-fatal errors ({errors.length})</span>
            </div>
            <ul className="space-y-1 text-[11px] text-amber-900 font-mono max-h-40 overflow-y-auto">
              {errors.slice(0, 30).map((e, i) => <li key={i}>• {e}</li>)}
            </ul>
          </div>
        )}

        {/* ── LIVE ACTIVITY STREAM — terminal-style scroller ── */}
        <div className="rounded-xl overflow-hidden shadow-lg border border-[#0b1c30]/30 mb-6" data-testid="automation-activity-panel">
          <div className="bg-gradient-to-r from-[#050b14] via-[#0b1c30] to-[#0f1a2e] px-5 py-3 flex items-center justify-between border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
              </div>
              <Terminal className="w-3.5 h-3.5 text-violet-300" />
              <span className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-white/70">backend activity</span>
              {running && (
                <span className="flex items-center gap-1 text-[10px] text-emerald-300 font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  live
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-[10px] font-mono text-white/50">
              <span data-testid="automation-activity-count">{activity.length} events</span>
              {!autoScroll && (
                <button
                  onClick={() => {
                    setAutoScroll(true);
                    if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center gap-1 px-2 py-0.5 rounded bg-violet-500/20 text-violet-200 hover:bg-violet-500/30 border border-violet-400/30"
                  data-testid="automation-scroll-bottom"
                >
                  <ArrowDown className="w-3 h-3" /> resume tail
                </button>
              )}
            </div>
          </div>

          <div
            ref={logContainerRef}
            onScroll={handleLogScroll}
            data-testid="automation-activity-log"
            className="bg-[#050b14] relative max-h-[360px] overflow-y-scroll overflow-x-hidden px-4 py-3 font-mono text-[11.5px] leading-[1.55] auto-activity-scroll"
            style={{
              fontFamily: "'JetBrains Mono', 'Menlo', 'Monaco', 'Courier New', monospace",
              backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(124,58,237,0.06), transparent 55%), radial-gradient(circle at 80% 80%, rgba(56,189,248,0.05), transparent 55%)',
              scrollbarColor: '#7c3aed #0b1c30',
              scrollbarWidth: 'thin',
            }}
          >
            {activity.length === 0 ? (
              <div className="text-white/30 italic text-center py-10 flex flex-col items-center gap-2">
                <Zap className="w-6 h-6 text-white/20" />
                <div>Waiting for the backend to speak…</div>
                <div className="text-[10px] not-italic">start the automation above to begin streaming events</div>
              </div>
            ) : (
              <>
                {activity.map((e) => {
                  const meta = ACTIVITY_META[e.type] || ACTIVITY_META.info;
                  const Icon = meta.Icon;
                  const time = new Date(e.ts).toLocaleTimeString('en-GB', { hour12: false });
                  return (
                    <div
                      key={e.seq}
                      data-testid={`activity-entry-${e.seq}`}
                      className="flex items-start gap-2 py-[2px] px-1 rounded hover:bg-white/[0.02] transition-colors"
                      style={{ animation: 'activityFadeIn 0.25s ease-out' }}
                    >
                      <span className="text-white/30 shrink-0 select-none">{time}</span>
                      <span
                        className="shrink-0 inline-flex items-center gap-1 px-1.5 py-[1px] rounded border text-[9.5px] uppercase tracking-wider font-bold"
                        style={{ color: meta.color, backgroundColor: meta.bg, borderColor: meta.color + '33' }}
                      >
                        <Icon className="w-2.5 h-2.5" />
                        {meta.label}
                      </span>
                      <span className="text-white/85 break-words" style={{ color: meta.color === '#94a3b8' ? 'rgba(255,255,255,0.82)' : undefined }}>
                        {e.msg}
                      </span>
                    </div>
                  );
                })}
                {running && (
                  <div className="flex items-center gap-2 mt-1 text-violet-300/80">
                    <span className="inline-block w-2 h-3.5 bg-violet-300/80 align-middle" style={{ animation: 'cursorBlink 0.9s steps(1) infinite' }} />
                    <span className="text-white/40 text-[10px]">awaiting next event…</span>
                  </div>
                )}
                <div ref={logEndRef} />
              </>
            )}
            <style>{`
              @keyframes activityFadeIn {
                from { opacity: 0; transform: translateX(-3px); }
                to   { opacity: 1; transform: translateX(0); }
              }
              @keyframes cursorBlink { 50% { opacity: 0; } }
              /* Visible violet scrollbar for the activity log — overrides default thin/invisible scrollbar on dark backgrounds */
              .auto-activity-scroll::-webkit-scrollbar { width: 10px; }
              .auto-activity-scroll::-webkit-scrollbar-track {
                background: linear-gradient(180deg, #0b1c30 0%, #050b14 100%);
                border-left: 1px solid rgba(255,255,255,0.05);
              }
              .auto-activity-scroll::-webkit-scrollbar-thumb {
                background: linear-gradient(180deg, #7c3aed 0%, #38bdf8 100%);
                border-radius: 6px;
                border: 2px solid #050b14;
                box-shadow: 0 0 6px rgba(124,58,237,0.35);
              }
              .auto-activity-scroll::-webkit-scrollbar-thumb:hover {
                background: linear-gradient(180deg, #8b5cf6 0%, #60a5fa 100%);
                box-shadow: 0 0 10px rgba(124,58,237,0.55);
              }
            `}</style>
          </div>
        </div>

        {/* Sections log */}
        {sectionsDone.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-[#c3c6d7]/30 overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-[#c3c6d7]/20 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-[#434655]">Completed sections</span>
              <span className="text-[11px] font-mono text-[#737686]">{sectionsDone.length}</span>
            </div>
            <div className="divide-y divide-[#c3c6d7]/15 max-h-96 overflow-y-auto">
              {sectionsDone.map((s, i) => (
                <div key={`${s.key}-${i}`} className="px-5 py-2.5 flex items-center gap-3 hover:bg-[#f8f9ff]/60">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span className="text-xs font-semibold text-[#0b1c30] flex-1 truncate">{s.title}</span>
                  <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                    style={{ color: TIER_COLORS[s.tier] || '#434655', backgroundColor: (TIER_COLORS[s.tier] || '#c3c6d7') + '1A' }}>
                    {s.tier}
                  </span>
                  <span className="text-[10px] font-mono text-[#737686]">step {s.step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── RUN HISTORY — persisted across sessions ── */}
        <div className="bg-white rounded-xl shadow-sm border border-[#c3c6d7]/30 overflow-hidden" data-testid="automation-history-panel">
          <div className="px-5 py-3 border-b border-[#c3c6d7]/20 flex items-center justify-between bg-gradient-to-r from-[#f8f9ff] to-white flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <History className="w-4 h-4 text-[#7c3aed]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#434655]">Recent runs</span>
              <span className="text-[10px] font-mono text-[#737686]">last {visibleRuns.length}{countryFilter ? ` of ${runs.length}` : ''}</span>
              {countryFilter && (
                <button
                  onClick={() => setCountryFilter(null)}
                  data-testid="automation-clear-country-filter"
                  className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] border border-[#7c3aed]/30 hover:bg-[#7c3aed]/15 text-[10px] font-bold uppercase tracking-wider transition-colors"
                  title="Clear country filter"
                >
                  <Filter className="w-2.5 h-2.5" />
                  {countryFilter}
                  <XIcon className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
            <span className="text-[10px] text-[#737686]">auto-refreshes every {status.running ? '4' : '12'}s</span>
          </div>
          {visibleRuns.length === 0 ? (
            <div className="px-5 py-10 text-center text-[11px] text-[#737686] italic">
              {countryFilter ? `No runs for ${countryFilter} yet.` : 'No automation runs yet — start your first one above.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="automation-history-table">
                <thead className="bg-[#f8f9ff]/60 border-b border-[#c3c6d7]/20">
                  <tr className="text-[9.5px] font-bold uppercase tracking-wider text-[#737686]">
                    <th className="px-4 py-2.5 text-left">Country</th>
                    <th className="px-3 py-2.5 text-left">Status</th>
                    <th className="px-3 py-2.5 text-right">Duration</th>
                    <th className="px-3 py-2.5 text-right">Sections</th>
                    <th className="px-3 py-2.5 text-right">Errors</th>
                    <th className="px-3 py-2.5 text-right">Step</th>
                    <th className="px-3 py-2.5 text-right">Events</th>
                    <th className="px-3 py-2.5 text-left">Triggered by</th>
                    <th className="px-3 py-2.5 text-right">Started</th>
                    <th className="px-3 py-2.5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRuns.map((r) => {
                    const meta = RUN_STATUS_META[r.status] || RUN_STATUS_META.stopped;
                    const StatusIcon = meta.Icon;
                    const startedLabel = r.started_at ? new Date(r.started_at).toLocaleString(undefined, {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    }) : '—';
                    const errorCount = r.errors_count || 0;
                    return (
                      <tr key={r.id}
                          onClick={() => openRunDetail(r.id)}
                          className="border-b border-[#c3c6d7]/15 hover:bg-[#f8f9ff]/40 transition-colors cursor-pointer"
                          data-testid={`automation-history-row-${r.id}`}>
                        <td className="px-4 py-2.5 font-semibold text-[#0b1c30]">
                          <div className="flex items-center gap-2">
                            <Bot className="w-3 h-3 text-[#7c3aed]" />
                            <button
                              onClick={(e) => { e.stopPropagation(); setCountryFilter(countryFilter === r.country_name ? null : r.country_name); }}
                              data-testid={`automation-history-country-btn-${r.id}`}
                              title={countryFilter === r.country_name ? 'Clear filter' : `Filter to ${r.country_name} only`}
                              className="text-left hover:text-[#7c3aed] hover:underline transition-colors"
                            >
                              {r.country_name || '—'}
                            </button>
                          </div>
                          {r.sector && <div className="text-[9.5px] text-[#737686] font-normal truncate max-w-[180px] ml-5">{r.sector}</div>}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider border"
                            style={{ color: meta.color, backgroundColor: meta.bg, borderColor: meta.border }}
                            data-testid={`automation-history-status-${r.id}`}>
                            <StatusIcon className={`w-2.5 h-2.5 ${meta.spin ? 'animate-spin' : ''}`} />
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-[#434655]">
                          <div className="inline-flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5 text-[#737686]" />
                            {formatDuration(r.duration_seconds)}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: (r.sections_done_count || 0) > 0 ? '#059669' : '#94a3b8' }}>
                          {r.sections_done_count || 0}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: errorCount > 0 ? '#dc2626' : '#94a3b8' }}>
                          {errorCount}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-[#737686]">{r.last_step ?? '—'}/7</td>
                        <td className="px-3 py-2.5 text-right font-mono text-[#737686]">{r.activity_event_count ?? '—'}</td>
                        <td className="px-3 py-2.5 text-[#434655]">
                          <div className="inline-flex items-center gap-1.5">
                            <UserIcon className="w-2.5 h-2.5 text-[#737686]" />
                            <span className="truncate max-w-[140px]">{r.triggered_by?.name || r.triggered_by?.email || '—'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-[10px] text-[#737686]">{startedLabel}</td>
                        <td className="px-3 py-2.5 text-center">
                          {r.status !== 'running' ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); retryRun(r); }}
                              disabled={status.running}
                              data-testid={`automation-history-retry-${r.id}`}
                              title={status.running ? 'Stop the current run to retry' : `Re-run automation for ${r.country_name}`}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border transition-all
                                ${status.running
                                  ? 'text-[#c3c6d7] border-[#c3c6d7]/40 cursor-not-allowed bg-white/40'
                                  : 'text-[#7c3aed] border-[#7c3aed]/35 hover:bg-[#7c3aed]/10 hover:border-[#7c3aed]/60 hover:shadow-sm active:scale-95'
                                }`}
                            >
                              <RotateCw className={`w-2.5 h-2.5 ${status.running ? '' : 'group-hover:rotate-45'}`} />
                              Retry
                            </button>
                          ) : (
                            <span className="text-[9.5px] text-[#c3c6d7] font-mono italic">in-flight</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── RUN DETAIL SLIDE-OVER DRAWER ── */}
      {drawerRun && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          data-testid="automation-run-drawer"
          onClick={closeDrawer}
          style={{ animation: 'drawerFadeIn 0.2s ease-out' }}
        >
          {/* backdrop */}
          <div className="absolute inset-0 bg-[#0b1c30]/45 backdrop-blur-sm" />

          {/* panel */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[560px] h-full bg-white shadow-2xl overflow-y-auto"
            style={{ animation: 'drawerSlideIn 0.28s cubic-bezier(0.22,1,0.36,1)' }}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-br from-[#0b1c30] via-[#12263f] to-[#1a2d4a] text-white px-6 py-5 border-b border-white/10">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/50 mb-1">Automation run</div>
                  <div className="text-xl font-extrabold truncate">{drawerRun.country_name || '—'}</div>
                  {drawerRun.sector && <div className="text-[11px] text-white/70 truncate">{drawerRun.sector}</div>}
                </div>
                <button
                  onClick={closeDrawer}
                  data-testid="automation-drawer-close"
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0"
                  title="Close"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {drawerLoading && !drawerRun.status ? (
              <div className="flex items-center justify-center py-20 text-[#737686] text-xs gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading run…
              </div>
            ) : (
              <div className="p-6 space-y-5">
                {/* Status + metrics grid */}
                <div className="grid grid-cols-2 gap-3">
                  {(() => {
                    const meta = RUN_STATUS_META[drawerRun.status] || RUN_STATUS_META.stopped;
                    const StatusIcon = meta.Icon;
                    return (
                      <div className="col-span-2 flex items-center gap-2 p-3 rounded-lg border" style={{ backgroundColor: meta.bg, borderColor: meta.border }}>
                        <StatusIcon className={`w-4 h-4 ${meta.spin ? 'animate-spin' : ''}`} style={{ color: meta.color }} />
                        <span className="font-bold uppercase tracking-wider text-xs" style={{ color: meta.color }}>{meta.label}</span>
                        {drawerRun.progress_pct != null && (
                          <span className="ml-auto font-mono text-[11px] text-[#434655]">{drawerRun.progress_pct}%</span>
                        )}
                      </div>
                    );
                  })()}
                  <div className="p-3 rounded-lg border border-[#c3c6d7]/40 bg-[#f8f9ff]/40">
                    <div className="text-[9px] uppercase tracking-widest text-[#737686] mb-1">Duration</div>
                    <div className="text-lg font-bold font-mono text-[#0b1c30] flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-[#737686]" />
                      {formatDuration(drawerRun.duration_seconds)}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg border border-[#c3c6d7]/40 bg-[#f8f9ff]/40">
                    <div className="text-[9px] uppercase tracking-widest text-[#737686] mb-1">Last step</div>
                    <div className="text-lg font-bold font-mono text-[#0b1c30]">{drawerRun.last_step ?? '—'} / 7</div>
                  </div>
                  <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50">
                    <div className="text-[9px] uppercase tracking-widest text-emerald-700 mb-1">Sections generated</div>
                    <div className="text-lg font-bold font-mono text-emerald-700">{drawerRun.sections_done_count || 0}</div>
                  </div>
                  <div className="p-3 rounded-lg border border-red-200 bg-red-50" style={{ opacity: (drawerRun.errors_count || 0) === 0 ? 0.55 : 1 }}>
                    <div className="text-[9px] uppercase tracking-widest text-red-700 mb-1">Errors</div>
                    <div className="text-lg font-bold font-mono text-red-700">{drawerRun.errors_count || 0}</div>
                  </div>
                </div>

                {/* Triggered by */}
                <div className="p-3 rounded-lg border border-[#c3c6d7]/40 bg-white">
                  <div className="text-[9px] uppercase tracking-widest text-[#737686] mb-1.5">Triggered by</div>
                  <div className="flex items-center gap-2 text-xs text-[#434655]">
                    <UserIcon className="w-3 h-3 text-[#7c3aed]" />
                    <span className="font-semibold">{drawerRun.triggered_by?.name || '—'}</span>
                    {drawerRun.triggered_by?.email && <span className="text-[10px] text-[#737686] font-mono">· {drawerRun.triggered_by.email}</span>}
                  </div>
                  <div className="text-[10px] text-[#737686] mt-2 font-mono">
                    Started  {drawerRun.started_at ? new Date(drawerRun.started_at).toLocaleString() : '—'}
                  </div>
                  {drawerRun.finished_at && (
                    <div className="text-[10px] text-[#737686] font-mono">
                      Finished {new Date(drawerRun.finished_at).toLocaleString()}
                    </div>
                  )}
                  <div className="text-[10px] text-[#737686] mt-1 font-mono">Activity events  {drawerRun.activity_event_count ?? 0}</div>
                  <div className="text-[9px] text-[#c3c6d7] mt-1 font-mono break-all">run_id · {drawerRun.id}</div>
                </div>

                {/* Sections done */}
                <div className="rounded-lg border border-[#c3c6d7]/40 bg-white overflow-hidden" data-testid="automation-drawer-sections">
                  <div className="px-4 py-2.5 border-b border-[#c3c6d7]/20 flex items-center justify-between bg-[#f8f9ff]/50">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#434655]">Completed sections</span>
                    <span className="text-[10px] font-mono text-[#737686]">{(drawerRun.sections_done || []).length}</span>
                  </div>
                  {(drawerRun.sections_done || []).length === 0 ? (
                    <div className="px-4 py-6 text-center text-[10px] text-[#c3c6d7] italic">No sections generated during this run</div>
                  ) : (
                    <div className="divide-y divide-[#c3c6d7]/15 max-h-64 overflow-y-auto">
                      {(drawerRun.sections_done || []).map((s, i) => (
                        <div key={`${s.key}-${i}`} className="px-4 py-2 flex items-center gap-2 text-[11px]">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                          <span className="flex-1 truncate text-[#0b1c30]">{s.title}</span>
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                                style={{ color: TIER_COLORS[s.tier] || '#434655', backgroundColor: (TIER_COLORS[s.tier] || '#c3c6d7') + '1A' }}>
                            {s.tier}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Errors */}
                <div className="rounded-lg border border-[#c3c6d7]/40 bg-white overflow-hidden" data-testid="automation-drawer-errors">
                  <div className="px-4 py-2.5 border-b border-[#c3c6d7]/20 flex items-center justify-between bg-red-50/50">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-red-800">Errors</span>
                    <span className="text-[10px] font-mono text-red-700">{(drawerRun.errors || []).length}</span>
                  </div>
                  {(drawerRun.errors || []).length === 0 ? (
                    <div className="px-4 py-4 text-center text-[10px] text-emerald-600 italic">Clean run — zero errors</div>
                  ) : (
                    <ul className="divide-y divide-red-100 max-h-64 overflow-y-auto text-[10.5px] font-mono">
                      {drawerRun.errors.map((e, i) => (
                        <li key={i} className="px-4 py-2 text-red-800">• {e}</li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between gap-3 pt-2">
                  {drawerRun.status !== 'running' && drawerRun.status && (
                    <button
                      onClick={() => { retryRun(drawerRun); closeDrawer(); }}
                      disabled={status.running}
                      data-testid="automation-drawer-retry"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-all
                                 text-[#7c3aed] border-[#7c3aed]/35 hover:bg-[#7c3aed]/10 hover:border-[#7c3aed]/60 hover:shadow-sm active:scale-95
                                 disabled:text-[#c3c6d7] disabled:border-[#c3c6d7]/40 disabled:cursor-not-allowed disabled:bg-white/40"
                    >
                      <RotateCw className="w-3 h-3" />
                      Retry this run
                    </button>
                  )}
                  <button
                    onClick={closeDrawer}
                    className="ml-auto text-xs text-[#737686] hover:text-[#434655] underline-offset-2 hover:underline"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
          <style>{`
            @keyframes drawerFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes drawerSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
          `}</style>
        </div>
      )}
    </div>
  );
}
